import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Download, ExternalLink, Loader2 } from "lucide-react";
import {
  Orgs,
  Proposals,
  Subscriptions,
  type OrgFiscal,
  type Proposal,
  type TaxIdType,
} from "../../lib/apiTyped";
import { useAuth } from "../../hooks/useAuth";
import { useOrgSnapshot } from "../../hooks/useOrgSnapshot";
import { useResource } from "../../lib/useResource";
import { useApiCall } from "../../lib/useToastError";
import { useNotifications } from "../../components/shared/NotificationsProvider";
import { PageHeader } from "../../components/AppLayout";
import { Card } from "../../components/shared/Card";
import { FormInput } from "../../components/shared/FormInput";
import { Button } from "../../components/shared/Button";
import { StatusBadge } from "../../components/shared/StatusBadge";
import { Spinner } from "../../components/shared/Spinner";
import { PlansShowcase } from "../../components/shared/PlansShowcase";
import { SubscriptionAgreementCheckbox } from "../../components/shared/SubscriptionAgreementCheckbox";
import { TrialStatusBanner } from "../../components/shared/TrialStatusBanner";
import { fmtEur, fmtDate } from "../../lib/format";

const LIVE_SUB_STATUSES = new Set([
  "active",
  "trialing",
  "past_due",
  "incomplete",
]);

// Single page that owns every billing concern: current subscription
// summary, pending one-time payments, the org's tax identity (legal
// name, address, tax id, billing email) and the historical Stripe
// invoices (subscription cycles + paid one-offs together). Replaces the
// old /settings/fiscal and /payments/proposals pages.

const COUNTRIES: Array<[string, string]> = [
  ["ES", "Spain"],
  ["FR", "France"],
  ["DE", "Germany"],
  ["IT", "Italy"],
  ["PT", "Portugal"],
  ["NL", "Netherlands"],
  ["BE", "Belgium"],
  ["IE", "Ireland"],
  ["AT", "Austria"],
  ["LU", "Luxembourg"],
  ["DK", "Denmark"],
  ["FI", "Finland"],
  ["SE", "Sweden"],
  ["GB", "United Kingdom"],
  ["CH", "Switzerland"],
  ["NO", "Norway"],
  ["US", "United States"],
];

const TAX_ID_OPTIONS: Array<[TaxIdType, string]> = [
  ["es_cif", "Spanish CIF"],
  ["es_dni", "Spanish DNI / NIF"],
  ["eu_vat", "European VAT number"],
  ["gb_vat", "UK VAT number"],
  ["ch_vat", "Swiss VAT number"],
  ["no_vat", "Norway VAT number"],
  ["us_ein", "US EIN"],
];

function defaultTaxIdType(country: string): TaxIdType {
  if (country === "ES") return "es_cif";
  if (country === "GB") return "gb_vat";
  if (country === "CH") return "ch_vat";
  if (country === "US") return "us_ein";
  if (country === "NO") return "no_vat";
  return "eu_vat";
}

export function OwnerBilling() {
  const { user } = useAuth();
  const { orgId, org, loading } = useOrgSnapshot();
  const { run } = useApiCall();
  const { toast } = useNotifications();
  const existing = (org as { fiscal?: OrgFiscal & { taxIdType?: TaxIdType } } | null)
    ?.fiscal;
  const consentRecorded = Boolean(
    (org as { fiscalConsent?: unknown } | null)?.fiscalConsent,
  );
  const hasFiscal = Boolean(existing);
  const termsAlreadyAccepted = Boolean(
    (org as { subscriptionTermsAcceptedAt?: unknown } | null)
      ?.subscriptionTermsAcceptedAt,
  );
  const contactContext = {
    orgId: orgId ?? undefined,
    orgName: ((org as { name?: string } | null)?.name) ?? undefined,
    email: user?.email ?? undefined,
  };

  // Stripe Checkout returns to /billing with ?paid=<sessionId> after a
  // successful one-off payment, ?activated=1 after a subscription is
  // activated, or ?canceled=1 when the user backs out of either flow.
  // We bump `refreshKey` so data-loading children refetch — the Stripe
  // webhook writes the resulting state and usually lands within ~1s of
  // the redirect but isn't guaranteed to be there on the first read.
  // Second bump at 2s covers the gap.
  const [searchParams, setSearchParams] = useSearchParams();
  const [refreshKey, setRefreshKey] = useState(0);
  useEffect(() => {
    const paid = searchParams.get("paid");
    const activated = searchParams.get("activated") === "1";
    const canceled = searchParams.get("canceled") === "1";
    if (!paid && !activated && !canceled) return;
    if (paid) {
      toast("Pago recibido. La factura aparecerá en breve.", "success");
      setRefreshKey((k) => k + 1);
      const timer = setTimeout(() => setRefreshKey((k) => k + 1), 2000);
      const next = new URLSearchParams(searchParams);
      next.delete("paid");
      setSearchParams(next, { replace: true });
      return () => clearTimeout(timer);
    }
    if (activated) {
      toast("Suscripción activada. ¡Ya puedes invitar a tu equipo!", "success");
      setRefreshKey((k) => k + 1);
      const timer = setTimeout(() => setRefreshKey((k) => k + 1), 2000);
      const next = new URLSearchParams(searchParams);
      next.delete("activated");
      setSearchParams(next, { replace: true });
      return () => clearTimeout(timer);
    }
    toast("Operación cancelada. No se ha cobrado nada.", "info");
    const next = new URLSearchParams(searchParams);
    next.delete("canceled");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, toast]);

  if (loading || !orgId) {
    return (
      <div className="flex items-center justify-center py-20">
        <Spinner />
      </div>
    );
  }

  return (
    <>
      <PageHeader
        title="Billing"
        subtitle="Plan, invoices, billing details and Tax ID in one place."
      />

      <div className="space-y-6">
        <TrialStatusBanner org={org} orgId={orgId} email={user?.email ?? undefined} />
        {!existing && (
          <Card
            className="p-6 border-2"
            style={{ borderColor: "rgba(200,169,81,0.5)" }}
          >
            <h2 className="text-lg font-black text-ink mb-2">
              Completa tu información fiscal
            </h2>
            <p className="text-sm text-ink-2">
              Sin estos datos no podemos emitir facturas ni activar tu
              suscripción. Rellena los bloques de abajo (razón social,
              dirección y NIF) y guarda — tardas un par de minutos.
            </p>
          </Card>
        )}
        <SubscriptionSection
          orgId={orgId}
          refreshKey={refreshKey}
          hasFiscal={hasFiscal}
          termsAlreadyAccepted={termsAlreadyAccepted}
          contactContext={contactContext}
        />
        <PendingPayments refreshKey={refreshKey} />
        <InvoicesList
          orgId={orgId}
          hasFiscal={hasFiscal}
          refreshKey={refreshKey}
        />
        <BillingEmailSection
          orgId={orgId}
          existing={existing}
          consentRecorded={consentRecorded}
          onSaved={() => run(() => Promise.resolve(null))}
        />
        <BillingAddressSection
          orgId={orgId}
          existing={existing}
          consentRecorded={consentRecorded}
        />
        <TaxIdSection
          orgId={orgId}
          existing={existing}
          consentRecorded={consentRecorded}
        />
      </div>
    </>
  );
}

// ───────────────────────────────────────────────────────────────────────
// Subscription section — current plan, activation / portal / cancel, and
// the plan catalog so the owner can request a different tier.

function SubscriptionSection({
  orgId,
  refreshKey,
  hasFiscal,
  termsAlreadyAccepted,
  contactContext,
}: {
  orgId: string;
  refreshKey: number;
  hasFiscal: boolean;
  termsAlreadyAccepted: boolean;
  contactContext: { orgId?: string; orgName?: string; email?: string };
}) {
  const { data, loading, refetch } = useResource(
    () => Subscriptions.get(orgId),
    [orgId, refreshKey],
  );
  const { run } = useApiCall();
  const { confirm } = useNotifications();
  const [termsAccepted, setTermsAccepted] = useState(false);

  const sub = data?.subscription ?? null;
  const tier = data?.tier ?? null;
  const seatCount = data?.seatCount ?? 0;
  const status = data?.subscriptionStatus ?? null;
  const live = status ? LIVE_SUB_STATUSES.has(status) : false;
  const canActivate = hasFiscal && (termsAlreadyAccepted || termsAccepted);

  async function activate() {
    if (!termsAlreadyAccepted) {
      const accepted = await run(() => Subscriptions.acceptTerms(orgId));
      if (!accepted) return;
    }
    const result = await run(() => Subscriptions.checkout(orgId));
    if (result?.checkoutUrl) window.location.href = result.checkoutUrl;
  }
  async function openPortal() {
    const result = await run(() => Subscriptions.portal(orgId));
    if (result?.portalUrl) window.location.href = result.portalUrl;
  }
  async function cancelSub() {
    const ok = await confirm({
      title: "Cancel subscription",
      message:
        "It will stay active until the end of the current billing cycle. You won't be charged again.",
      confirmLabel: "Cancel at period end",
      variant: "danger",
    });
    if (!ok) return;
    await run(() => Subscriptions.cancel(orgId), {
      successMessage: "Cancellation scheduled.",
    });
    await refetch();
  }

  return (
    <>
      <Card className="p-6">
        <h2 className="text-lg font-black text-ink mb-4">Subscription</h2>
        {loading || !data ? (
          <p className="text-sm text-ink-3">—</p>
        ) : !data.activeTier ? (
          <p className="text-sm text-ink-2">
            No plan yet. Choose one below and request it from the Formular team —
            we'll assign tier and seats before you activate the subscription.
          </p>
        ) : (
          <>
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="flex flex-wrap items-center gap-x-8 gap-y-2 text-sm">
                <div>
                  <div className="text-xs text-ink-3 uppercase tracking-wider">
                    Plan
                  </div>
                  <div className="text-ink font-semibold">
                    {tier?.name ?? data.activeTier}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-ink-3 uppercase tracking-wider">
                    Price
                  </div>
                  <div className="text-ink font-semibold">
                    {fmtEur(tier?.priceEurPerMonth ?? 0)} / month
                  </div>
                </div>
                <div>
                  <div className="text-xs text-ink-3 uppercase tracking-wider">
                    Max users
                  </div>
                  <div className="text-ink font-semibold">{seatCount}</div>
                </div>
              </div>
              <div className="flex flex-col items-end gap-2">
                {/* Trial status lives in TrialStatusBanner above; here it
                    would surface as a misleading "pending" Stripe badge. */}
                {data.activeTier !== "trial" && (
                  <StatusBadge status={status ?? "pending"} />
                )}
                {sub?.cancelAtPeriodEnd && (
                  <span className="text-xs text-ink-3">
                    cancels at period end
                  </span>
                )}
                {sub?.currentPeriodEnd ? (
                  <span className="text-xs text-ink-3">
                    next: {fmtDate(sub.currentPeriodEnd)}
                  </span>
                ) : null}
              </div>
            </div>

            {/* Trial is intentionally Stripe-free. Hide the "activate
                payment / accept terms" UX entirely — the TrialStatusBanner
                above already handles the trial's own lifecycle. */}
            {data.activeTier !== "trial" && !live && !termsAlreadyAccepted && (
              <div className="mt-6">
                <SubscriptionAgreementCheckbox
                  accepted={termsAccepted}
                  onChange={setTermsAccepted}
                />
              </div>
            )}

            {data.activeTier !== "trial" && (
              <div className="flex flex-wrap gap-3 mt-6">
                {!live ? (
                  <Button
                    variant="primary"
                    onClick={activate}
                    disabled={!canActivate}
                  >
                    Activate &amp; choose payment method
                  </Button>
                ) : (
                  <>
                    <Button variant="ghost" onClick={openPortal}>
                      Manage payment method · invoices
                    </Button>
                    {!sub?.cancelAtPeriodEnd && (
                      <Button variant="link" onClick={cancelSub}>
                        Cancel at period end
                      </Button>
                    )}
                  </>
                )}
              </div>
            )}

            {status === "past_due" && (
              <p className="mt-4 text-xs" style={{ color: "#C61418" }}>
                Payment failed. Update your payment method from the portal to
                avoid suspension.
              </p>
            )}
          </>
        )}
      </Card>

      {!loading && data && (
        <div className="space-y-3">
          <h3 className="text-base font-black text-ink">
            {data.activeTier ? "Other plans" : "Available plans"}
          </h3>
          <p className="text-sm text-ink-2">
            {data.activeTier
              ? "Need more volume or something custom? Ask us for a change."
              : "Pick the plan that fits and request it — we'll get back to you."}
          </p>
          <PlansShowcase
            currentTierSlug={data.activeTier ?? undefined}
            contactContext={contactContext}
          />
        </div>
      )}
    </>
  );
}

// ───────────────────────────────────────────────────────────────────────
// Pending one-time payments (proposals waiting to be paid)

function PendingPayments({ refreshKey }: { refreshKey: number }) {
  const [pending, setPending] = useState<Proposal[]>([]);
  useEffect(() => {
    let cancelled = false;
    Proposals.list()
      .then((res) => {
        if (cancelled) return;
        setPending(res.proposals.filter((p) => p.status === "sent"));
      })
      .catch(() => {
        /* noop */
      });
    return () => {
      cancelled = true;
    };
  }, [refreshKey]);
  if (pending.length === 0) return null;
  return (
    <Card className="p-6">
      <h2 className="text-lg font-black text-ink mb-4">Pending payments</h2>
      <ul className="divide-y divide-border">
        {pending.map((p) => (
          <li key={p.id} className="py-3 flex items-center justify-between gap-3">
            <div>
              <div className="text-sm text-ink">{p.concept}</div>
              <div className="text-xs text-ink-3">{fmtDate(p.createdAt)}</div>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm text-ink font-semibold">
                {fmtEur(p.amountEur)} <span className="text-ink-3 font-normal">+ IVA</span>
              </span>
              {p.paymentLink ? (
                <a
                  href={p.paymentLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-ink text-white text-xs font-semibold rounded-full px-3 py-1.5 hover:opacity-80"
                >
                  Pay now
                </a>
              ) : (
                <StatusBadge status={p.status} />
              )}
            </div>
          </li>
        ))}
      </ul>
    </Card>
  );
}

// ───────────────────────────────────────────────────────────────────────
// Invoices — subscription cycles + paid one-offs together

function InvoicesList({
  orgId,
  hasFiscal,
  refreshKey,
}: {
  orgId: string;
  hasFiscal: boolean;
  refreshKey: number;
}) {
  const [data, setData] = useState<Awaited<
    ReturnType<typeof Orgs.invoices>
  > | null>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!hasFiscal) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    Orgs.invoices(orgId)
      .then((d) => {
        if (!cancelled) {
          setData(d);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [orgId, hasFiscal, refreshKey]);

  return (
    <Card className="p-6">
      <h2 className="text-lg font-black text-ink mb-4">Invoices</h2>
      {!hasFiscal ? (
        <p className="text-sm text-ink-3">
          Complete your billing details to start receiving invoices.
        </p>
      ) : loading ? (
        <div className="py-6 flex justify-center">
          <Loader2 className="w-5 h-5 text-ink-3 animate-spin" />
        </div>
      ) : !data || data.invoices.length === 0 ? (
        <div className="text-center py-8">
          <div className="text-sm font-semibold text-ink mb-1">No invoices yet</div>
          <p className="text-xs text-ink-3">
            When a billing cycle or payment completes, you'll see it here.
          </p>
        </div>
      ) : (
        <ul className="divide-y divide-border">
          {data.invoices.map((inv) => (
            <li
              key={inv.id}
              className="py-3 flex items-center justify-between gap-3"
            >
              <div className="min-w-0">
                <div className="text-sm text-ink truncate">
                  {inv.description}
                </div>
                <div className="text-xs text-ink-3 flex flex-wrap gap-2">
                  <span>{inv.number ?? inv.id.slice(0, 8)}</span>
                  <span>·</span>
                  <span>{inv.kind === "subscription" ? "Subscription" : "One-time"}</span>
                  {inv.paidAt && (
                    <>
                      <span>·</span>
                      <span>{fmtDate(inv.paidAt)}</span>
                    </>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm text-ink font-semibold">
                  {fmtEur(inv.total)}
                </span>
                <StatusBadge status={inv.status} />
                {inv.pdfUrl && (
                  <a
                    href={inv.pdfUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-ink-2 hover:text-ink"
                    title="Download PDF"
                  >
                    <Download className="w-4 h-4" />
                  </a>
                )}
                {inv.hostedUrl && (
                  <a
                    href={inv.hostedUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-ink-2 hover:text-ink"
                    title="View in Stripe"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </a>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

// ───────────────────────────────────────────────────────────────────────
// Billing email section

function BillingEmailSection({
  orgId,
  existing,
  consentRecorded,
}: {
  orgId: string;
  existing: (OrgFiscal & { taxIdType?: TaxIdType }) | undefined;
  consentRecorded: boolean;
  onSaved: () => void;
}) {
  const [email, setEmail] = useState(existing?.emailFacturacion ?? "");
  const [busy, setBusy] = useState(false);
  const { run } = useApiCall();
  useEffect(() => {
    setEmail(existing?.emailFacturacion ?? "");
  }, [existing?.emailFacturacion]);
  if (!existing) return null;
  async function save() {
    if (!existing) return;
    setBusy(true);
    await run(
      () =>
        Orgs.updateFiscal(
          orgId,
          { ...existing, emailFacturacion: email } as OrgFiscal,
          { consent: consentRecorded ? undefined : { version: "v1", accepted: true } },
        ),
      { successMessage: "Details saved." },
    );
    setBusy(false);
  }
  return (
    <Card className="p-6">
      <h2 className="text-lg font-black text-ink">Billing email</h2>
      <p className="text-sm text-ink-3 mb-4">
        Invoices will be sent automatically to this email.
      </p>
      <FormInput label="Email" value={email} onChange={setEmail} type="email" />
      <div className="mt-4">
        <Button
          variant="primary"
          onClick={save}
          disabled={busy || email === existing.emailFacturacion}
        >
          {busy ? "Saving…" : "Save"}
        </Button>
      </div>
    </Card>
  );
}

// ───────────────────────────────────────────────────────────────────────
// Billing address section

function BillingAddressSection({
  orgId,
  existing,
  consentRecorded,
}: {
  orgId: string;
  existing: (OrgFiscal & { taxIdType?: TaxIdType }) | undefined;
  consentRecorded: boolean;
}) {
  const [razonSocial, setRazonSocial] = useState(existing?.razonSocial ?? "");
  const [pais, setPais] = useState(existing?.pais ?? "ES");
  const [line1, setLine1] = useState(existing?.direccionFiscal.line1 ?? "");
  const [line2, setLine2] = useState(existing?.direccionFiscal.line2 ?? "");
  const [city, setCity] = useState(existing?.direccionFiscal.city ?? "");
  const [postalCode, setPostalCode] = useState(
    existing?.direccionFiscal.postalCode ?? "",
  );
  const [region, setRegion] = useState(existing?.direccionFiscal.region ?? "");
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const { run } = useApiCall();

  useEffect(() => {
    if (!existing) return;
    setRazonSocial(existing.razonSocial);
    setPais(existing.pais);
    setLine1(existing.direccionFiscal.line1);
    setLine2(existing.direccionFiscal.line2 ?? "");
    setCity(existing.direccionFiscal.city);
    setPostalCode(existing.direccionFiscal.postalCode);
    setRegion(existing.direccionFiscal.region ?? "");
  }, [existing]);

  const needsConsent = !consentRecorded;
  const canSave = !busy && (!needsConsent || consent);

  async function save() {
    if (!canSave) return;
    setBusy(true);
    const fiscal: OrgFiscal = {
      nif: existing?.nif ?? "",
      taxIdType: existing?.taxIdType,
      razonSocial,
      direccionFiscal: {
        line1,
        line2: line2 || null,
        city,
        postalCode,
        region: region || null,
      },
      pais,
      emailFacturacion: existing?.emailFacturacion ?? "",
    };
    await run(
      () =>
        Orgs.updateFiscal(orgId, fiscal, {
          consent: needsConsent ? { version: "v1", accepted: true } : undefined,
        }),
      { successMessage: "Details saved." },
    );
    setBusy(false);
  }

  return (
    <Card className="p-6">
      <h2 className="text-lg font-black text-ink">Billing address</h2>
      <p className="text-sm text-ink-3 mb-4">
        Will appear on every invoice issued from Stripe.
      </p>
      <div className="space-y-4">
        <FormInput
          label="Legal name"
          value={razonSocial}
          onChange={setRazonSocial}
        />
        <div>
          <label className="block text-xs font-semibold mb-1 text-ink-3">
            Country or region
          </label>
          <select
            value={pais}
            onChange={(e) => setPais(e.target.value)}
            className="w-full rounded-xl px-3 py-2.5 text-sm focus:outline-none"
            style={{
              backgroundColor: "rgba(255,255,255,0.6)",
              border: "1px solid rgba(0,0,0,0.14)",
              color: "#0f0f0f",
            }}
          >
            {COUNTRIES.map(([code, name]) => (
              <option key={code} value={code}>
                {name}
              </option>
            ))}
          </select>
        </div>
        <FormInput label="Address line 1" value={line1} onChange={setLine1} />
        <FormInput
          label="Address line 2 (optional)"
          value={line2}
          onChange={setLine2}
        />
        <div className="grid grid-cols-2 gap-3">
          <FormInput label="City" value={city} onChange={setCity} />
          <FormInput
            label="Postal code"
            value={postalCode}
            onChange={setPostalCode}
          />
        </div>
        <FormInput
          label="Region (optional)"
          value={region}
          onChange={setRegion}
        />
      </div>

      {needsConsent && (
        <div className="mt-5 pt-5 border-t border-border">
          <label className="flex items-start gap-3 cursor-pointer text-sm text-ink-2">
            <input
              type="checkbox"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              className="mt-0.5 w-4 h-4 accent-ink shrink-0"
            />
            <span>
              I agree that this data is processed in accordance with the{" "}
              <Link
                to="/legal/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="underline text-ink hover:opacity-80"
              >
                Privacy Policy
              </Link>{" "}
              to issue invoices and create my customer profile in Stripe
              Payments Europe.
            </span>
          </label>
        </div>
      )}

      <div className="mt-5">
        <Button variant="primary" onClick={save} disabled={!canSave}>
          {busy ? "Saving…" : "Save"}
        </Button>
      </div>
    </Card>
  );
}

// ───────────────────────────────────────────────────────────────────────
// Tax ID section

function TaxIdSection({
  orgId,
  existing,
  consentRecorded,
}: {
  orgId: string;
  existing: (OrgFiscal & { taxIdType?: TaxIdType }) | undefined;
  consentRecorded: boolean;
}) {
  const [type, setType] = useState<TaxIdType>(
    existing?.taxIdType ?? defaultTaxIdType(existing?.pais ?? "ES"),
  );
  const [value, setValue] = useState(existing?.nif ?? "");
  const [busy, setBusy] = useState(false);
  const { run } = useApiCall();

  useEffect(() => {
    setType(existing?.taxIdType ?? defaultTaxIdType(existing?.pais ?? "ES"));
    setValue(existing?.nif ?? "");
  }, [existing?.taxIdType, existing?.pais, existing?.nif]);

  const needsConsent = !consentRecorded;

  async function save() {
    if (!existing || busy) return;
    setBusy(true);
    const fiscal: OrgFiscal = {
      ...existing,
      nif: value,
      taxIdType: type,
    };
    await run(
      () =>
        Orgs.updateFiscal(orgId, fiscal, {
          consent: needsConsent ? { version: "v1", accepted: true } : undefined,
        }),
      { successMessage: "Tax ID saved." },
    );
    setBusy(false);
  }

  if (!existing) return null;

  return (
    <Card className="p-6">
      <h2 className="text-lg font-black text-ink">Tax ID</h2>
      <p className="text-sm text-ink-3 mb-4">
        Appears as the tax identifier on every invoice.
      </p>
      <div className="flex flex-col sm:flex-row gap-3">
        <select
          value={type}
          onChange={(e) => setType(e.target.value as TaxIdType)}
          className="rounded-xl px-3 py-2.5 text-sm focus:outline-none sm:w-56"
          style={{
            backgroundColor: "rgba(255,255,255,0.6)",
            border: "1px solid rgba(0,0,0,0.14)",
            color: "#0f0f0f",
          }}
        >
          {TAX_ID_OPTIONS.map(([code, name]) => (
            <option key={code} value={code}>
              {name}
            </option>
          ))}
        </select>
        <input
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={type === "es_cif" ? "B12345678" : "DE123456789"}
          className="flex-1 rounded-xl px-3 py-2.5 text-sm focus:outline-none"
          style={{
            backgroundColor: "rgba(255,255,255,0.6)",
            border: "1px solid rgba(0,0,0,0.14)",
            color: "#0f0f0f",
          }}
        />
      </div>
      <div className="mt-4">
        <Button
          variant="primary"
          onClick={save}
          disabled={
            busy ||
            (value === existing.nif &&
              type === (existing.taxIdType ?? defaultTaxIdType(existing.pais)))
          }
        >
          {busy ? "Saving…" : "Save"}
        </Button>
      </div>
    </Card>
  );
}
